<template>
  <div></div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, shallowRef } from 'vue'
import * as THREE from 'three'

interface RoomBoundaryProps {
  width?: number
  depth?: number
  height?: number
  wallColor?: string
  wallOpacity?: number
}

const props = withDefaults(defineProps<RoomBoundaryProps>(), {
  width: 4,
  depth: 5,
  height: 2.8,
  wallColor: '#446688',
  wallOpacity: 0.15,
})

// 创建主组 - 使用shallowRef避免Vue深度代理Three.js对象
const group = shallowRef(new THREE.Group())

// 导出group，供父组件使用
defineExpose({
  group,
})

// 计算墙壁的位置和尺寸
const wallWidth = computed(() => props.width)
const wallDepth = computed(() => props.depth)
const wallHeight = computed(() => props.height)

// 创建房间边界
const createRoomBoundary = () => {
  // 清空现有元素
  while (group.value.children.length > 0) {
    group.value.remove(group.value.children[0])
  }

  const halfWidth = wallWidth.value / 2
  const halfDepth = wallDepth.value / 2
  const height = wallHeight.value

  const wallMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color(props.wallColor),
    transparent: true,
    opacity: props.wallOpacity,
    side: THREE.DoubleSide,
    depthWrite: false,
  })

  const edgeMaterial = new THREE.LineBasicMaterial({
    color: 0x6688aa,
    transparent: true,
    opacity: 0.5,
  })

  // 创建四堵墙 - 墙面垂直放置，从地面(z=0)向上延伸
  // Three.js坐标系：Z轴垂直向上，X轴水平，Y轴深度（垂直屏幕向外）
  // 调整墙面位置，确保底部与地面(z=0)齐平，顶部向上延伸

  // 前墙 (y = -halfDepth) - 面向屏幕外（Y轴正方向）的墙
  const frontWallGeo = new THREE.PlaneGeometry(wallWidth.value, height)
  const frontWall = new THREE.Mesh(frontWallGeo, wallMaterial)
  // 旋转90度使其与Y轴垂直，面向Y轴负方向
  frontWall.rotation.x = -Math.PI / 2
  // 位置：中心在z=height/2, y=-halfDepth
  frontWall.position.set(0, -halfDepth, height / 2)
  group.value.add(frontWall)

  // 后墙 (y = halfDepth) - 面向屏幕内（Y轴负方向）的墙
  const backWallGeo = new THREE.PlaneGeometry(wallWidth.value, height)
  const backWall = new THREE.Mesh(backWallGeo, wallMaterial)
  // 旋转90度使其与Y轴垂直，面向Y轴正方向
  backWall.rotation.x = -Math.PI / 2
  // 位置：中心在z=height/2, y=halfDepth
  backWall.position.set(0, halfDepth, height / 2)
  group.value.add(backWall)

  // 左墙 (x = -halfWidth) - 面向X轴正方向的墙
  const leftWallGeo = new THREE.PlaneGeometry(wallDepth.value, height)
  const leftWall = new THREE.Mesh(leftWallGeo, wallMaterial)
  // 旋转90度使其与X轴垂直，面向X轴正方向
  leftWall.rotation.x = -Math.PI / 2
  leftWall.rotation.y = Math.PI / 2
  // 位置：中心在z=height/2, x=-halfWidth
  leftWall.position.set(-halfWidth, 0, height / 2)
  group.value.add(leftWall)

  // 右墙 (x = halfWidth) - 面向X轴负方向的墙
  const rightWallGeo = new THREE.PlaneGeometry(wallDepth.value, height)
  const rightWall = new THREE.Mesh(rightWallGeo, wallMaterial)
  // 旋转90度使其与X轴垂直，面向X轴负方向
  rightWall.rotation.x = -Math.PI / 2
  rightWall.rotation.y = -Math.PI / 2
  // 位置：中心在z=height/2, x=halfWidth
  rightWall.position.set(halfWidth, 0, height / 2)
  group.value.add(rightWall)

  // 创建边缘线 - 确保底部在z=0，顶部在z=height
  const edgePoints = [
    // 底部边框（z=0）
    new THREE.Vector3(-halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, halfDepth, 0),
    new THREE.Vector3(halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, -halfDepth, 0),
    // 顶部边框（z=height）
    new THREE.Vector3(-halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, halfDepth, height),
    new THREE.Vector3(halfWidth, halfDepth, height),
    new THREE.Vector3(-halfWidth, halfDepth, height),
    new THREE.Vector3(-halfWidth, halfDepth, height),
    new THREE.Vector3(-halfWidth, -halfDepth, height),
    // 垂直边框
    new THREE.Vector3(-halfWidth, -halfDepth, 0),
    new THREE.Vector3(-halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, halfDepth, 0),
    new THREE.Vector3(halfWidth, halfDepth, height),
    new THREE.Vector3(-halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, halfDepth, height),
  ]

  const edgeGeometry = new THREE.BufferGeometry().setFromPoints(edgePoints)
  const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial)
  group.value.add(edges)
}

// 监听属性变化，更新房间边界
watch(
  () => [props.width, props.depth, props.height, props.wallColor, props.wallOpacity],
  () => {
    createRoomBoundary()
  },
  { deep: true }
)

// 挂载时创建房间边界
onMounted(() => {
  createRoomBoundary()
})
</script>

<style scoped lang="less"></style>
